import { supabase } from './supabase';
import type {
  Goal,
  Ladder,
  LadderStep,
  LadderWithSteps,
  CurrentFocusStep,
  MostActiveLadderPath,
} from '../types/ladder';

export async function fetchGoalsWithLadders(userId: string): Promise<Goal[]> {
  const { data: goals, error: goalsError } = await supabase
    .from('goals')
    .select('*')
    .eq('user_id', userId)
    .order('is_priority', { ascending: false })
    .order('position', { ascending: true });

  if (goalsError) throw goalsError;
  return goals || [];
}

export async function fetchLaddersForGoal(goalId: string): Promise<LadderWithSteps[]> {
  const { data: ladders, error: laddersError } = await supabase
    .from('ladders')
    .select('*')
    .eq('goal_id', goalId)
    .order('level_number', { ascending: false });

  if (laddersError) throw laddersError;

  if (!ladders || ladders.length === 0) return [];

  const ladderIds = ladders.map((l) => l.id);
  const { data: steps, error: stepsError } = await supabase
    .from('ladder_steps')
    .select('*')
    .in('ladder_id', ladderIds)
    .order('step_number', { ascending: true });

  if (stepsError) throw stepsError;

  const laddersWithSteps: LadderWithSteps[] = ladders.map((ladder) => ({
    ...ladder,
    steps: (steps || []).filter((step) => step.ladder_id === ladder.id),
  }));

  return laddersWithSteps;
}

export function findDeepestIncompleteLadder(
  ladders: LadderWithSteps[],
  allSteps: LadderStep[]
): LadderWithSteps | null {
  const activeLadders = ladders.filter((l) => l.status === 'active');
  if (activeLadders.length === 0) return null;

  let deepestLadder: LadderWithSteps | null = null;
  let maxLevel = -1;

  for (const ladder of activeLadders) {
    const hasIncompleteSteps = ladder.steps.some((s) => !s.is_completed);
    if (hasIncompleteSteps && ladder.level_number > maxLevel) {
      maxLevel = ladder.level_number;
      deepestLadder = ladder;
    }
  }

  if (!deepestLadder) return null;

  const childLadders = activeLadders.filter((l) => {
    const parentStep = allSteps.find((s) => s.id === l.parent_step_id);
    return parentStep && parentStep.ladder_id === deepestLadder!.id;
  });

  if (childLadders.length > 0) {
    return findDeepestIncompleteLadder(childLadders, allSteps);
  }

  return deepestLadder;
}

export function getCurrentFocusStep(ladder: LadderWithSteps): LadderStep | null {
  const incompleteSteps = ladder.steps
    .filter((s) => !s.is_completed)
    .sort((a, b) => a.step_number - b.step_number);

  return incompleteSteps.length > 0 ? incompleteSteps[0] : null;
}

export function buildLadderPath(
  targetLadder: LadderWithSteps,
  allLadders: LadderWithSteps[]
): LadderWithSteps[] {
  const path: LadderWithSteps[] = [targetLadder];
  let currentLadder = targetLadder;

  while (currentLadder.parent_step_id) {
    const parentLadder = allLadders.find((l) =>
      l.steps.some((s) => s.id === currentLadder.parent_step_id)
    );

    if (!parentLadder) break;

    path.unshift(parentLadder);
    currentLadder = parentLadder;
  }

  return path;
}

export async function getMostActiveLadderPath(
  goalId: string
): Promise<MostActiveLadderPath | null> {
  const { data: goal, error: goalError } = await supabase
    .from('goals')
    .select('*')
    .eq('id', goalId)
    .maybeSingle();

  if (goalError || !goal) return null;

  const ladders = await fetchLaddersForGoal(goalId);
  if (ladders.length === 0) return null;

  const allSteps = ladders.flatMap((l) => l.steps);
  const deepestLadder = findDeepestIncompleteLadder(ladders, allSteps);

  if (!deepestLadder) {
    return {
      goal,
      ladderPath: [],
      currentFocusStep: null,
    };
  }

  const ladderPath = buildLadderPath(deepestLadder, ladders);
  const currentFocusStep = getCurrentFocusStep(deepestLadder);

  return {
    goal,
    ladderPath,
    currentFocusStep,
  };
}

export async function getCurrentFocusStepForGoal(
  goalId: string
): Promise<CurrentFocusStep | null> {
  const activePath = await getMostActiveLadderPath(goalId);
  if (!activePath || !activePath.currentFocusStep) return null;

  const deepestLadder = activePath.ladderPath[activePath.ladderPath.length - 1];

  return {
    step: activePath.currentFocusStep,
    ladder: deepestLadder,
    goal: activePath.goal,
  };
}

export async function createGoalWithInitialLadder(
  userId: string,
  goalName: string,
  alignment: string,
  finalOutcome: string,
  steps: string[]
): Promise<{ goal: Goal; ladder: Ladder; steps: LadderStep[] } | null> {
  const { data: goal, error: goalError } = await supabase
    .from('goals')
    .insert({
      user_id: userId,
      name: goalName,
      alignment: alignment,
      final_outcome: finalOutcome,
      status: 'active',
    })
    .select()
    .single();

  if (goalError || !goal) {
    console.error('Error creating goal:', goalError);
    return null;
  }

  const { data: ladder, error: ladderError } = await supabase
    .from('ladders')
    .insert({
      goal_id: goal.id,
      outcome_text: finalOutcome,
      level_number: 1,
      status: 'active',
    })
    .select()
    .single();

  if (ladderError || !ladder) {
    console.error('Error creating ladder:', ladderError);
    return null;
  }

  const stepRecords = steps.map((desc, index) => ({
    ladder_id: ladder.id,
    step_number: 5 - index,
    description: desc,
    is_completed: false,
  }));

  const { data: createdSteps, error: stepsError } = await supabase
    .from('ladder_steps')
    .insert(stepRecords)
    .select();

  if (stepsError || !createdSteps) {
    console.error('Error creating steps:', stepsError);
    return null;
  }

  return { goal, ladder, steps: createdSteps };
}

export async function updateStepCompletion(
  stepId: string,
  isCompleted: boolean
): Promise<boolean> {
  const { error } = await supabase
    .from('ladder_steps')
    .update({
      is_completed: isCompleted,
      completed_at: isCompleted ? new Date().toISOString() : null,
    })
    .eq('id', stepId);

  if (error) {
    console.error('Error updating step completion:', error);
    return false;
  }

  return true;
}

export async function checkAndCompleteLadder(ladderId: string): Promise<boolean> {
  const { data: steps, error: stepsError } = await supabase
    .from('ladder_steps')
    .select('*')
    .eq('ladder_id', ladderId);

  if (stepsError || !steps) return false;

  const allComplete = steps.every((s) => s.is_completed);

  if (allComplete) {
    const { error: updateError } = await supabase
      .from('ladders')
      .update({
        status: 'completed',
        completed_at: new Date().toISOString(),
      })
      .eq('id', ladderId);

    if (updateError) {
      console.error('Error completing ladder:', updateError);
      return false;
    }

    return true;
  }

  return false;
}

export async function createNestedLadder(
  parentStepId: string,
  outcomeText: string,
  steps: string[]
): Promise<{ ladder: Ladder; steps: LadderStep[] } | null> {
  const { data: parentStep, error: stepError } = await supabase
    .from('ladder_steps')
    .select('*, ladders!ladder_steps_ladder_id_fkey!inner(*)')
    .eq('id', parentStepId)
    .maybeSingle();

  if (stepError || !parentStep) {
    console.error('Error fetching parent step:', stepError);
    return null;
  }

  const parentLadder = (parentStep as any).ladders;
  const newLevelNumber = parentLadder.level_number + 1;

  const { data: ladder, error: ladderError } = await supabase
    .from('ladders')
    .insert({
      goal_id: parentLadder.goal_id,
      outcome_text: outcomeText,
      level_number: newLevelNumber,
      parent_step_id: parentStepId,
      status: 'active',
    })
    .select()
    .single();

  if (ladderError || !ladder) {
    console.error('Error creating nested ladder:', ladderError);
    return null;
  }

  const stepRecords = steps.map((desc, index) => ({
    ladder_id: ladder.id,
    step_number: 5 - index,
    description: desc,
    is_completed: false,
  }));

  const { data: createdSteps, error: stepsError } = await supabase
    .from('ladder_steps')
    .insert(stepRecords)
    .select();

  if (stepsError || !createdSteps) {
    console.error('Error creating steps:', stepsError);
    return null;
  }

  return { ladder, steps: createdSteps };
}

export async function updateStepDescription(
  stepId: string,
  description: string
): Promise<boolean> {
  const { error } = await supabase
    .from('ladder_steps')
    .update({ description })
    .eq('id', stepId);

  if (error) {
    console.error('Error updating step description:', error);
    return false;
  }

  return true;
}

export async function deleteLadder(ladderId: string): Promise<boolean> {
  const { error } = await supabase
    .from('ladders')
    .delete()
    .eq('id', ladderId);

  if (error) {
    console.error('Error deleting ladder:', error);
    return false;
  }

  return true;
}

export async function createGoalOnly(
  userId: string,
  goalName: string,
  alignment: string,
  finalOutcome: string
): Promise<Goal | null> {
  const { data: goal, error: goalError } = await supabase
    .from('goals')
    .insert({
      user_id: userId,
      name: goalName,
      alignment: alignment,
      final_outcome: finalOutcome,
      status: 'active',
    })
    .select()
    .single();

  if (goalError || !goal) {
    console.error('Error creating goal:', goalError);
    return null;
  }

  return goal;
}

export async function deleteGoal(goalId: string): Promise<boolean> {
  const { error } = await supabase
    .from('goals')
    .delete()
    .eq('id', goalId);

  if (error) {
    console.error('Error deleting goal:', error);
    return false;
  }

  return true;
}

export async function createLadderWithSteps(
  goalId: string,
  outcomeText: string,
  levelNumber: number,
  parentStepId: string | null,
  steps: string[]
): Promise<{ ladder: Ladder; steps: LadderStep[] } | null> {
  const { data: ladder, error: ladderError } = await supabase
    .from('ladders')
    .insert({
      goal_id: goalId,
      outcome_text: outcomeText,
      level_number: levelNumber,
      parent_step_id: parentStepId,
      status: 'active',
    })
    .select()
    .single();

  if (ladderError || !ladder) {
    console.error('Error creating ladder:', ladderError);
    return null;
  }

  const stepRecords = steps.map((desc, index) => ({
    ladder_id: ladder.id,
    step_number: 5 - index,
    description: desc,
    is_completed: false,
  }));

  const { data: createdSteps, error: stepsError } = await supabase
    .from('ladder_steps')
    .insert(stepRecords)
    .select();

  if (stepsError || !createdSteps) {
    console.error('Error creating steps:', stepsError);
    return null;
  }

  return { ladder, steps: createdSteps };
}

export async function fetchLadderPathByStepId(
  stepId: string
): Promise<{ goal: Goal; ladderPath: LadderWithSteps[]; parentStep: LadderStep } | null> {
  const { data: parentStep, error: stepError } = await supabase
    .from('ladder_steps')
    .select('*, ladders!ladder_steps_ladder_id_fkey!inner(*)')
    .eq('id', stepId)
    .maybeSingle();

  if (stepError || !parentStep) {
    console.error('Error fetching parent step:', stepError);
    return null;
  }

  const parentLadder = (parentStep as any).ladders;

  const { data: goal, error: goalError } = await supabase
    .from('goals')
    .select('*')
    .eq('id', parentLadder.goal_id)
    .maybeSingle();

  if (goalError || !goal) {
    console.error('Error fetching goal:', goalError);
    return null;
  }

  const allLadders = await fetchLaddersForGoal(goal.id);
  const currentLadder = allLadders.find((l) => l.id === parentLadder.id);

  if (!currentLadder) {
    return null;
  }

  const ladderPath = buildLadderPath(currentLadder, allLadders);

  return {
    goal,
    ladderPath,
    parentStep: parentStep as LadderStep,
  };
}

export async function fetchGoalById(goalId: string): Promise<Goal | null> {
  const { data: goal, error } = await supabase
    .from('goals')
    .select('*')
    .eq('id', goalId)
    .maybeSingle();

  if (error || !goal) {
    console.error('Error fetching goal:', error);
    return null;
  }

  return goal;
}

export async function getAllLowestRungSteps(userId: string): Promise<CurrentFocusStep[]> {
  const goals = await fetchGoalsWithLadders(userId);
  const lowestSteps: CurrentFocusStep[] = [];

  for (const goal of goals) {
    const focusStep = await getCurrentFocusStepForGoal(goal.id);
    if (focusStep) {
      lowestSteps.push(focusStep);
    }
  }

  return lowestSteps;
}

export async function toggleGoalPriority(
  userId: string,
  goalId: string,
  isPriority: boolean
): Promise<boolean> {
  if (isPriority) {
    const { data: priorityGoals, error: countError } = await supabase
      .from('goals')
      .select('id')
      .eq('user_id', userId)
      .eq('is_priority', true);

    if (countError) {
      console.error('Error counting priority goals:', countError);
      return false;
    }

    if (priorityGoals && priorityGoals.length >= 3) {
      console.error('Maximum of 3 priority goals allowed');
      return false;
    }
  }

  const { error } = await supabase
    .from('goals')
    .update({ is_priority: isPriority })
    .eq('id', goalId)
    .eq('user_id', userId);

  if (error) {
    console.error('Error updating goal priority:', error);
    return false;
  }

  return true;
}
